<script lang="ts">
  import { onMount } from "svelte";
  import { resizableColumns } from "$lib/columnWidths.svelte";
  import { _ } from "svelte-i18n";
  import AppShell from "$components/AppShell.svelte";
  import { api } from "$lib/api";

  let { params }: { params: { slug: string } } = $props();
  const slug = $derived(params?.slug ?? "");

  type Category = { id: number; name: string };
  type SupplierLinkInfo = {
    id: number;
    buyer_tenant: { slug: string; display_name: string; country: string } | null;
    seller_tenant: { slug: string; display_name: string; country: string } | null;
  };
  type VisibilityData = {
    supplier_link_id: number;
    select_all_categories: boolean;
    visible_category_ids: number[];
  };

  let categories = $state<Category[]>([]);
  let links = $state<SupplierLinkInfo[]>([]);
  let visibilities = $state<Map<number, VisibilityData>>(new Map());
  let loading = $state(true);
  let saving = $state<number | null>(null);
  let countryFilter = $state("");
  let countries = $derived(
    [...new Set(links.map(l => l.buyer_tenant?.country || l.seller_tenant?.country || "").filter(Boolean))].sort()
  );
  let filteredLinks = $derived(
    countryFilter ? links.filter(l => (l.buyer_tenant?.country || l.seller_tenant?.country) === countryFilter) : links
  );

  async function load() {
    loading = true;
    try {
      const [cats, allLinks] = await Promise.all([
        api.get<Category[]>(`/t/${slug}/categories`),
        api.get<SupplierLinkInfo[]>(`/t/${slug}/supplier-links`),
      ]);
      categories = cats;
      // Access is granted by the seller — a link where we are the buying
      // side has nothing to edit here and only shows up as our own company.
      const lks = allLinks.filter((l) => l.seller_tenant?.slug === slug);
      links = lks;
      const visArr = await Promise.all(
        lks.map((l: any) =>
          api.get<VisibilityData>(`/t/${slug}/supplier-links/${l.id}/visibility`).catch(() => null)
        )
      );
      const m = new Map<number, VisibilityData>();
      visArr.forEach((v, i) => { if (v) m.set(lks[i].id, v); });
      visibilities = m;
    } catch (e) {
      console.error(e);
    } finally {
      loading = false;
    }
  }

  function isChecked(linkId: number, catId: number): boolean {
    const vis = visibilities.get(linkId);
    if (!vis) return false;
    if (vis.select_all_categories) return true;
    return vis.visible_category_ids.includes(catId);
  }

  async function toggleCategory(linkId: number, catId: number) {
    saving = linkId;
    const vis = visibilities.get(linkId) ?? { supplier_link_id: linkId, select_all_categories: false, visible_category_ids: [] };
    let newIds = vis.visible_category_ids.slice();
    if (newIds.includes(catId)) {
      newIds = newIds.filter(id => id !== catId);
    } else {
      newIds.push(catId);
    }
    try {
      const updated = await api.patch<VisibilityData>(`/t/${slug}/supplier-links/${linkId}/visibility`, {
        select_all_categories: false,
        visible_category_ids: newIds,
        visible_product_ids: [],
      });
      visibilities = new Map(visibilities).set(linkId, updated);
    } catch (e) { console.error(e); }
    finally { saving = null; }
  }

  async function toggleAll(linkId: number) {
    saving = linkId;
    const vis = visibilities.get(linkId);
    const newSelectAll = !vis?.select_all_categories;
    try {
      const updated = await api.patch<VisibilityData>(`/t/${slug}/supplier-links/${linkId}/visibility`, {
        select_all_categories: newSelectAll,
        visible_category_ids: [],
        visible_product_ids: [],
      });
      visibilities = new Map(visibilities).set(linkId, updated);
    } catch (e) { console.error(e); }
    finally { saving = null; }
  }

  function partnerName(link: SupplierLinkInfo): string {
    return link.buyer_tenant?.display_name || `Link #${link.id}`;
  }

  onMount(() => { if (slug) load(); });
</script>

<AppShell tenantSlug={slug} active="pricing/access">
  <div class="am-page">
    <div class="am-header">
      <div>
        <h1 class="am-title">{$_("nav.pricingAccess")}</h1>
        <p class="am-desc">Control which categories are visible to each distributor.</p>
      </div>
      {#if countries.length > 1}
        <select class="country-filter" bind:value={countryFilter}>
          <option value="">All countries</option>
          {#each countries as c}<option value={c}>{c}</option>{/each}
        </select>
      {/if}
    </div>

    {#if loading}
      <div class="loading">{$_("common.loading")}</div>
    {:else if categories.length === 0 || links.length === 0}
      <div class="empty-state">
        <span class="material-icons-outlined empty-icon">lock_open</span>
        <p>You need at least one product category and one linked distributor to use the access matrix.</p>
      </div>
    {:else}
      <div class="matrix-wrap">
        <table use:resizableColumns={"access-matrix"} class="matrix-table">
          <thead>
            <tr>
              <th class="cat-col">Category</th>
              {#each filteredLinks as link}
                <th class="link-col" title={partnerName(link)}>
                  <div class="link-header">
                    <span class="link-name">{partnerName(link)}</span>
                    <label class="all-check" title="Select all categories">
                      <input type="checkbox"
                        checked={visibilities.get(link.id)?.select_all_categories ?? false}
                        disabled={saving === link.id}
                        onchange={() => toggleAll(link.id)} />
                      <span class="all-label">All</span>
                    </label>
                  </div>
                </th>
              {/each}
            </tr>
          </thead>
          <tbody>
            {#each categories as cat}
              <tr>
                <td class="cat-cell">{cat.name}</td>
                {#each filteredLinks as link}
                  <td class="check-cell">
                    <input type="checkbox"
                      checked={isChecked(link.id, cat.id)}
                      disabled={saving === link.id}
                      onchange={() => toggleCategory(link.id, cat.id)} />
                  </td>
                {/each}
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>
</AppShell>

<style>
  .am-page { display: flex; flex-direction: column; gap: 20px; }
  .am-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
  .am-title { margin: 0; font-size: 22px; font-weight: 700; }
  .am-desc { margin: 0; font-size: 14px; color: var(--color-text-muted); }
  .country-filter { padding: 7px 12px; border: 1px solid var(--color-divider); border-radius: 6px; font-size: 13px; font-family: inherit; }
  .loading { padding: 48px; text-align: center; color: var(--color-text-muted); }
  .empty-state { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 64px; color: var(--color-text-muted); }
  .empty-icon { font-size: 48px !important; opacity: 0.3; }
  .matrix-wrap { background: #fff; border: 1px solid var(--color-divider); border-radius: 8px; overflow: auto; }
  .matrix-table { border-collapse: collapse; font-size: 13px; min-width: 100%; }
  .matrix-table th { padding: 10px 14px; border-bottom: 1px solid var(--color-divider); background: #fafafa; }
  .matrix-table td { padding: 8px 14px; border-bottom: 1px solid var(--color-divider); }
  .matrix-table tr:last-child td { border-bottom: none; }
  .cat-col { text-align: left; font-size: 11px; font-weight: 600; color: var(--color-text-muted); text-transform: uppercase; letter-spacing: 0.05em; min-width: 160px; }
  .link-col { text-align: center; min-width: 120px; }
  .link-header { display: flex; flex-direction: column; align-items: center; gap: 4px; }
  .link-name { font-size: 11px; font-weight: 600; color: var(--color-surface-darker); max-width: 100px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .all-check { display: flex; align-items: center; gap: 3px; font-size: 10px; color: var(--color-text-muted); cursor: pointer; }
  .cat-cell { font-weight: 500; color: var(--color-text); }
  .check-cell { text-align: center; }
  .check-cell input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; accent-color: var(--color-primary); }
</style>
